import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowPathIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';
import { isAxiosError } from 'axios';
import api from '@/services/api';

interface GateReport {
  status: string; passed: boolean; activation_allowed: boolean; config_version: string;
  catalog_sequence: number;
  activation?: { event_id: string; activated_at: string; actor_user_id: string;
    event_hash: string; record_valid: boolean; sharing_phase: 'shadow' | 'live' } | null;
  window_start?: string; window_end?: string; ready_at?: string;
  criteria?: { minimum_wear_ratio: number; minimum_workers: number };
  included_workers?: number; included_worker_days?: number;
  excluded_baseline_days?: number; excluded_paused_days?: number; excluded_exception_days?: number;
  withdrawn_workers_excluded?: number; missing_days?: number;
  expected_hours?: number | null; evaluable_hours?: number | null; wear_ratio?: number | null;
  blockers?: string[]; report_digest?: string;
}

const labels: Record<string, string> = {
  PASS: 'Gate passed', FAIL: 'Gate not passed', WAITING_FOR_GRACE: 'Awaiting sync grace period',
  SYNTHETIC_EXCLUDED: 'Synthetic tenant excluded', SIGNED_GATE_POLICY_MISSING: 'Signed gate policy required',
  EVALUATION_DATES_INVALID: 'Evaluation dates or timezone require review',
  SYNC_GRACE_NOT_FINISHED: 'Sync grace period has not ended', MISSING_DAY_HISTORY: 'Day history is incomplete',
  INSUFFICIENT_COHORT: 'Too few included workers', NO_EXPECTED_HOURS: 'No eligible expected hours',
  WEAR_BELOW_THRESHOLD: 'Pooled wear is below the required threshold',
};

export default function EvaluationGatePanel({ userId }: { userId: string }) {
  const client = useQueryClient();
  const [confirmed, setConfirmed] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [audit, setAudit] = useState<string | null>(null);
  const busy = useRef(false);
  const attempt = useRef<{digest: string; key: string} | null>(null);
  const query = useQuery({ queryKey: ['evaluation-gate', userId], retry: false,
    queryFn: async () => (await api.get<GateReport>('/evaluation/report')).data });
  const report = query.data;
  useEffect(() => { setConfirmed(false); setAudit(null); }, [report?.report_digest]);
  useEffect(() => { if (report?.activation?.record_valid) setError(''); }, [report?.activation?.event_id, report?.activation?.record_valid]);
  async function activate() {
    if (busy.current || !confirmed || !report?.activation_allowed || !report.report_digest || query.isError) return;
    busy.current = true; setPending(true); setError('');
    try {
      if (attempt.current?.digest !== report.report_digest) {
        attempt.current = {digest: report.report_digest, key: crypto.randomUUID()};
      }
      await api.post('/evaluation/activate', {confirmed: true, request_key: attempt.current.key,
        reviewed_report_digest: report.report_digest, config_version: report.config_version,
        catalog_sequence: report.catalog_sequence});
      setConfirmed(false);
      await client.invalidateQueries();
    } catch (failure) {
      setError(isAxiosError(failure) && typeof failure.response?.data?.detail === 'string'
        ? failure.response.data.detail : 'Activation was not confirmed. Refresh to check its status before retrying.');
      setConfirmed(false);
      await query.refetch();
    } finally { busy.current = false; setPending(false); }
  }
  async function verifyAudit() {
    if (busy.current) return;
    busy.current = true; setPending(true); setAudit(null);
    try {
      const result = (await api.get<{valid: boolean; event_count: number}>('/evaluation/audit/verify')).data;
      setAudit(result.valid && result.event_count > 0 ? 'Organization activation audit verified' : 'Audit verification failed');
    } catch { setAudit('Audit verification unavailable'); }
    finally { busy.current = false; setPending(false); }
  }
  const hours = (value: number | null | undefined) => value == null ? 'Not calculated' : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return <section aria-labelledby="evaluation-heading">
    <div className="sv-toolbar"><h2 id="evaluation-heading">Evaluation gate</h2>
      <button title="Refresh evaluation report" aria-label="Refresh evaluation report" disabled={query.isFetching || pending} onClick={() => { setConfirmed(false); void query.refetch(); }}><ArrowPathIcon /></button></div>
    {query.isError ? <p role="alert">Evaluation report unavailable. Sharing activation is not available.</p>
      : !report ? <p role="status">Loading evaluation report...</p> : <>
        <p role="status">{labels[report.status] ?? 'Evaluation requires review'}</p>
        <p>Sharing activation: {report.activation ? report.activation.sharing_phase === 'live'
          ? 'Recorded - live' : 'Recorded - sharing currently held' : report.activation_allowed ? 'Awaiting administrator authorization' : 'Not available'}</p>
        {report.activation && <>
          <p>Activated: {new Date(report.activation.activated_at).toLocaleString()}</p>
          <p>Administrator ID: {report.activation.actor_user_id}</p>
          <button className="sv-gate-command" disabled={pending} onClick={() => void verifyAudit()}><CheckBadgeIcon />Verify activation audit</button>
          {audit && <p role="status">{audit}</p>}
        </>}
        <dl className="sv-gate-metrics">
          <div><dt>Signed configuration</dt><dd>{report.config_version}</dd></div>
          {report.window_start && <div><dt>Worker-local window</dt><dd>{report.window_start} to {report.window_end}</dd></div>}
          {report.ready_at && <div><dt>Grace period ends</dt><dd>{new Date(report.ready_at).toLocaleString()}</dd></div>}
          {report.criteria && <>
            <div><dt>Required wear</dt><dd>{report.criteria.minimum_wear_ratio * 100}%</dd></div>
            <div><dt>Minimum workers</dt><dd>{report.criteria.minimum_workers}</dd></div>
          </>}
          {report.included_workers !== undefined && <>
            <div><dt>Included workers</dt><dd>{report.status === 'WAITING_FOR_GRACE' ? 'Not calculated' : report.included_workers}</dd></div>
            <div><dt>Included worker-days</dt><dd>{report.status === 'WAITING_FOR_GRACE' ? 'Not calculated' : report.included_worker_days}</dd></div>
            <div><dt>Evaluable worn hours</dt><dd>{hours(report.evaluable_hours)}</dd></div>
            <div><dt>Expected hours</dt><dd>{hours(report.expected_hours)}</dd></div>
            <div><dt>Pooled wear compliance</dt><dd>{report.wear_ratio == null ? 'Not calculated' : `${(report.wear_ratio * 100).toFixed(2)}%`}</dd></div>
          </>}
          {report.status !== 'WAITING_FOR_GRACE' && report.excluded_baseline_days !== undefined && <>
            <div><dt>Pre-baseline / completion days excluded</dt><dd>{report.excluded_baseline_days}</dd></div>
            <div><dt>Paused days excluded</dt><dd>{report.excluded_paused_days}</dd></div>
            <div><dt>Exception days excluded</dt><dd>{report.excluded_exception_days}</dd></div>
            <div><dt>Withdrawn baseline-complete workers excluded</dt><dd>{report.withdrawn_workers_excluded}</dd></div>
            <div><dt>Missing day records</dt><dd>{report.missing_days}</dd></div>
          </>}
        </dl>
        {!!report.blockers?.length && <ul>{report.blockers.map(reason => <li key={reason}>{labels[reason] ?? 'Evidence requires review'}</li>)}</ul>}
        {!report.activation && report.activation_allowed && <form className="sv-toolbar" onSubmit={event => { event.preventDefault(); void activate(); }}>
          <label><input type="checkbox" checked={confirmed} disabled={pending || query.isFetching}
            onChange={event => setConfirmed(event.target.checked)} />I have reviewed this report and authorize live sharing for this organization.</label>
          <button className="sv-gate-command" type="submit" disabled={!confirmed || pending || query.isFetching}>
            <CheckBadgeIcon />{pending ? 'Activating...' : 'Activate live sharing'}</button>
        </form>}
      </>}
    {error && <p role="alert">{error}</p>}
  </section>;
}
